import { useCallback } from 'react';

export interface TopicBusinessUnit {
  id: string;
  name: string;
  type: 'Topic' | 'BU' | 'Campaign';
  business_unit_id?: string;
  created_at?: string;
}

// No base Campaign BU options - only topics from database
export const BASE_CAMPAIGN_BU_OPTIONS: string[] = [];

export const useCascadingLogic = (topicsBusinessUnits: any[]) => {
  // Helper function to get business unit for a campaign
  const getBusinessUnitForCampaign = useCallback((campaignName: string): string | null => {
    const campaign = topicsBusinessUnits.find(item => 
      item.type === 'Campaign' && item.name === campaignName
    );
    
    if (!campaign || !campaign.business_unit_id) return null;
    
    const businessUnit = topicsBusinessUnits.find(bu => 
      bu.type === 'BU' && bu.id === campaign.business_unit_id
    );
    
    return businessUnit ? businessUnit.name : null;
  }, [topicsBusinessUnits]);

  // Helper function to get campaign for a topic
  const getCampaignForTopic = useCallback((topicName: string): string | null => {
    // Ensure topicName is a string before calling replace
    if (typeof topicName !== 'string') {
      console.warn('getCampaignForTopic received non-string value:', topicName);
      return null;
    }
    
    // Remove the [Campaign Name] or [BU Name] badge from the topic name to get the clean topic name
    const cleanTopicName = topicName.replace(/\s*\[.*?\]\s*$/, '');
    
    const topic = topicsBusinessUnits.find(item => 
      item.type === 'Topic' && item.name === cleanTopicName
    );
    
    if (!topic || !topic.business_unit_id) return null;
    
    const campaign = topicsBusinessUnits.find(c => 
      c.type === 'Campaign' && c.id === topic.business_unit_id
    );
    
    return campaign ? campaign.name : null;
  }, [topicsBusinessUnits]);

  // Helper function to get business unit for a topic (through campaign)
  const getBusinessUnitForTopic = useCallback((topicName: string): string | null => {
    // Ensure topicName is a string
    if (typeof topicName !== 'string') {
      console.warn('getBusinessUnitForTopic received non-string value:', topicName);
      return null;
    }
    
    const campaignName = getCampaignForTopic(topicName);
    if (!campaignName) return null;
    
    return getBusinessUnitForCampaign(campaignName);
  }, [getCampaignForTopic, getBusinessUnitForCampaign]);

  // Helper function to get campaigns for a business unit
  const getCampaignsForBusinessUnit = useCallback((businessUnitName: string): string[] => {
    const businessUnit = topicsBusinessUnits.find(bu => 
      bu.type === 'BU' && bu.name === businessUnitName
    );
    
    if (!businessUnit) return [];
    
    return topicsBusinessUnits
      .filter(item => item.type === 'Campaign' && item.business_unit_id === businessUnit.id)
      .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
      .map(campaign => campaign.name);
  }, [topicsBusinessUnits]);

  // Helper function to get topics for a campaign
  const getTopicsForCampaign = useCallback((campaignName: string): string[] => {
    const campaign = topicsBusinessUnits.find(c => 
      c.type === 'Campaign' && c.name === campaignName
    );
    
    if (!campaign) return [];
    
    return topicsBusinessUnits
      .filter(item => item.type === 'Topic' && item.business_unit_id === campaign.id)
      .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
      .map(topic => topic.name);
  }, [topicsBusinessUnits]);

  // Helper function to get all topics for a business unit (through campaigns)
  const getTopicsForBusinessUnit = useCallback((businessUnitName: string): string[] => {
    const campaigns = getCampaignsForBusinessUnit(businessUnitName);
    const allTopics: string[] = [];
    
    campaigns.forEach(campaignName => {
      const campaignTopics = getTopicsForCampaign(campaignName);
      allTopics.push(...campaignTopics);
    });
    
    return allTopics;
  }, [getCampaignsForBusinessUnit, getTopicsForCampaign]);

  // Helper function to get available business units based on campaign BU selection
  const getAvailableBusinessUnits = useCallback((campaignBu: string, businessUnitOptions: string[]): string[] => {
    if (!campaignBu) return businessUnitOptions;
    
    // Get the parent business unit for the selected campaign/topic
    const parentBU = getBusinessUnitForTopic(campaignBu);
    if (parentBU) {
      return [parentBU];
    }
    
    return businessUnitOptions;
  }, [getBusinessUnitForTopic]);

  // Helper function to get available campaign BUs (topics) based on business unit selection
  const getAvailableCampaignBUs = useCallback((businessUnit: string, campaignBuOptions: string[]): string[] => {
    if (!businessUnit) return campaignBuOptions;
    
    // Get all topics that belong to this business unit (through campaigns)
    const topicsForBU = getTopicsForBusinessUnit(businessUnit);
    
    return topicsForBU;
  }, [getTopicsForBusinessUnit]);

  // NEW: Get all campaigns and topics for multi-select with proper formatting
  const getAllCampaignsAndTopics = (): Array<{value: string; label: string; type: 'Campaign' | 'Topic' | 'Content ID'}> => {
    const campaigns = topicsBusinessUnits
      .filter(item => item.type === 'Campaign')
      .map(campaign => ({
        value: campaign.name,
        label: campaign.name,
        type: 'Campaign' as const
      }));
    
    const topics = topicsBusinessUnits
      .filter(item => item.type === 'Topic')
      .map(topic => ({
        value: topic.name,
        label: topic.name,
        type: 'Topic' as const
      }));

    return [...campaigns, ...topics];
  };

  // NEW: Get business unit for selected items (campaigns/topics)
  const getBusinessUnitForSelectedItems = useCallback((selectedItems: string[]): string | null => {
    if (!selectedItems || !selectedItems.length) return null;

    // Get all business units for the selected items
    const businessUnits = new Set<string>();
    
    selectedItems.forEach(item => {
      // Ensure item is a string
      if (typeof item !== 'string') {
        console.warn('getBusinessUnitForSelectedItems received non-string item:', item);
        return;
      }
      
      // Check if it's a campaign
      const campaign = topicsBusinessUnits.find(c => c.type === 'Campaign' && c.name === item);
      if (campaign) {
        const bu = getBusinessUnitForCampaign(item);
        if (bu) businessUnits.add(bu);
        return;
      }

      // Check if it's a topic
      const topic = topicsBusinessUnits.find(t => t.type === 'Topic' && t.name === item);
      if (topic) {
        const bu = getBusinessUnitForTopic(item);
        if (bu) businessUnits.add(bu);
      }
    });

    // If all selected items belong to the same business unit, return it
    if (businessUnits.size === 1) {
      return Array.from(businessUnits)[0];
    }

    return null; // Mixed business units or no clear business unit
  }, [topicsBusinessUnits, getBusinessUnitForCampaign, getBusinessUnitForTopic]);

  return {
    getBusinessUnitForTopic,
    getCampaignForTopic,
    getBusinessUnitForCampaign,
    getCampaignsForBusinessUnit,
    getTopicsForCampaign,
    getTopicsForBusinessUnit,
    getAvailableBusinessUnits,
    getAvailableCampaignBUs,
    getAllCampaignsAndTopics,
    getBusinessUnitForSelectedItems
  };
};
